import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {of, Subscription} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';
import {MaterialModule} from '../../../material.module';
import {
    ConnectConnectionsService,
    ConnectServerConnection
} from '../../../services/connect/connect-connections.service';
import {ConnectProviderDef, ConnectProvidersService} from '../../../services/connect/connect-providers.service';
import {formatHttpErrorMessage} from '../../../services/connect/http-error-message';
import {ToastService} from '../../../services/layout/toast.service';

@Component({
  selector: 'app-connect-connection-detail',
  standalone: true,
    imports: [RouterModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './connect-connection-detail.component.html',
  styleUrls: ['../connect-shared.scss', './connect-connection-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'connect-theme'},
})
export class ConnectConnectionDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly connectionsService = inject(ConnectConnectionsService);
  private readonly providersService = inject(ConnectProvidersService);
  private readonly toastService = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private sub = new Subscription();

  loading = false;
  saving = false;
  creating = false;
  error: string | null = null;
  connection: ConnectServerConnection | null = null;
  providers: ConnectProviderDef[] = [];

  readonly form = this.fb.group({
    name: ['', [Validators.required]],
      providerId: [''],
      ref: ['custom.oauth2', [Validators.required]],
    mode: ['API'],
  });

  ngOnInit(): void {
    this.sub.add(
      this.route.paramMap.subscribe(params => {
        const id = params.get('id');
        if (id && id !== 'new') {
          this.load(id);
        } else {
          this.creating = true;
        }
      }),
    );
    this.loadProviders();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

    providerDocsUrl(providerId: string | null | undefined): string {
        if (!providerId) return '#';
        const provider = this.providers.find(p => p.id === providerId);
        return provider?.docsUrl || '#';
    }

    onProviderChanged(providerId: string): void {
        const provider = this.providers.find(p => p.id === providerId);
        if (!provider) {
            const currentRef = String(this.form.get('ref')?.value ?? '').trim();
            const isCatalogRef = currentRef ? this.providers.some(p => p.ref === currentRef) : false;
            if (!currentRef || currentRef.startsWith('custom.') || isCatalogRef) {
                this.form.patchValue({ref: 'custom.oauth2'});
            }
            this.cdr.markForCheck();
            return;
        }
        this.form.patchValue({ref: provider.ref || ''});
        this.cdr.markForCheck();
    }

  save(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
      const ref = String(this.form.value.ref || '').trim();
    const payload = {
      name: this.form.value.name!,
        ref,
      mode: this.form.value.mode || 'API',
        provider_ref: ref || undefined,
    };
    this.saving = true;
    this.error = null;
    const obs = this.creating
      ? this.connectionsService.create({...payload})
      : this.connectionsService.update(this.connection?.id || '', payload);
    obs
      .pipe(
        catchError(err => {
          const msg = formatHttpErrorMessage(err, 'Could not save connection.');
          console.error('Failed to save connection', err);
          this.error = msg;
          this.toastService.showError(msg);
          this.cdr.markForCheck();
          return of(null);
        }),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(result => {
        if (!result) return;
        this.connection = result as ConnectServerConnection;
        this.creating = false;
        this.toastService.showSuccess('Connection saved', 2500);
        this.router.navigate(['/connections']);
      });
  }

  private loadProviders(): void {
    this.providersService.fetchCatalog().pipe(
      catchError(err => {
        const msg = formatHttpErrorMessage(err, 'Could not load providers.');
        console.error('Failed to load providers', err);
        this.toastService.showError(msg);
        return of({version: 'v1', providers: []} as const);
      })
    ).subscribe(catalog => {
      this.providers = [...(catalog.providers || [])];
        this.patchProviderIdFromRef();
      this.cdr.markForCheck();
    });
  }

  private load(id: string): void {
    this.loading = true;
    this.connectionsService
      .get(id)
      .pipe(
        catchError(err => {
          const msg = formatHttpErrorMessage(err, 'Could not load connection.');
          console.error('Failed to load connection', err);
          this.error = msg;
          this.toastService.showError(msg);
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(conn => {
        if (!conn) return;
        this.connection = conn;
        this.form.patchValue({
          name: conn.name,
          ref: conn.ref,
          mode: (conn as any).mode || (conn.oauth_connected ? 'OAuth' : 'API'),
        });
          this.patchProviderIdFromRef();
        this.cdr.markForCheck();
      });
  }

    private patchProviderIdFromRef(): void {
        if (!this.providers.length) return;
        const currentProviderId = String(this.form.get('providerId')?.value ?? '').trim();
        if (currentProviderId) return;
        const ref = String(this.form.get('ref')?.value ?? '').trim();
        if (!ref || ref.startsWith('custom.')) return;
        const match = this.providers.find(p => p.ref === ref);
        if (!match?.id) return;
        this.form.patchValue({providerId: match.id}, {emitEvent: false});
    }
}
